import { createQiniuCallback, createQiniuPresignedToken } from '@/lib/api/attachmentApi'
import { UploadFileAsset, uploadToQiniu, UploadToQiniuResponseType } from '@/lib/api/qiniuApi'
import { Attachment } from '@/typings/domain'
import { nanoid } from '@reduxjs/toolkit'
import { useCallback, useImperativeHandle, useRef, useState } from 'react'
import UploadPrefix from './UploadPrefix'

export type UploadFileItemStatus =
  | { status: 'uploading'; progress: number }
  | { status: 'success'; attachment: Attachment }
  | { status: 'error' }

export type UploadFileItem = {
  id: string
  file: UploadFileAsset
} & UploadFileItemStatus

export type UploadFileChildrenProps = {
  uploadItems: UploadFileItem[]
} & UploadFileRef

export type UploadFileRef = {
  uploadFile: (file: UploadFileAsset, prefix: UploadPrefix) => Promise<Attachment>
  removeUploadItem: (id: string) => void
  removeAllUploadItems: () => void
}

type UploadFileProps = {
  ref?: React.RefObject<UploadFileRef | null>
  onSuccess?: (attachments: Attachment[]) => void
  children?: (props: UploadFileChildrenProps) => React.ReactNode
}

export default function UploadFile({ ref, onSuccess, children }: UploadFileProps) {
  const [items, setItems] = useState<UploadFileItem[]>([])
  const itemsStateRef = useRef<Record<string, { abort: () => void; attachmentId?: string }>>({})

  const successAttachmentsRef = useRef<Attachment[]>([])
  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess

  const upsertItem = useCallback<{
    (newItem: UploadFileItem): void
    (newItem: Pick<UploadFileItem, 'id'> & UploadFileItemStatus): void
  }>(newItem => {
    if (!itemsStateRef.current[newItem.id]) return

    setItems(items => {
      let found = false
      const newItems: UploadFileItem[] = items.map(item => {
        if (item.id === newItem.id) {
          found = true
          return { ...item, ...newItem }
        }
        return item
      })
      if (!found) {
        if (!('file' in newItem)) throw new Error()
        newItems.push(newItem)
      }
      return newItems
    })
  }, [])

  const uploadFile = useCallback<UploadFileChildrenProps['uploadFile']>(
    async (file, prefix) => {
      const id = nanoid()
      const abortController = new AbortController()
      itemsStateRef.current[id] = { abort: () => abortController.abort() }
      upsertItem({ id, file, status: 'uploading', progress: 0 })

      let token: Maybe<string> | undefined
      try {
        const { data } = await createQiniuPresignedToken({ prefix })
        token = data?.createPresignedToken?.token
        if (!token) {
          upsertItem({ id, status: 'error' })
          throw new Error('Failed to create presigned token')
        }
      } catch (error) {
        console.error('failed to create presigned token', error)
        upsertItem({ id, status: 'error' })
        throw error
      }

      let resp: UploadToQiniuResponseType
      try {
        resp = await uploadToQiniu({
          token,
          file,
          onProgress: progress => upsertItem({ id, status: 'uploading', progress }),
          abortController,
        })
      } catch (error) {
        console.error('failed to upload to qiniu', error)
        upsertItem({ id, status: 'error' })
        throw error
      }

      console.log('upload to qiniu response', resp)

      let attachment: Attachment
      try {
        const { data } = await createQiniuCallback({
          contentType: resp.content_type,
          fileWidth: resp.file_width,
          fileHeight: resp.file_height,
          fileName: resp.file_name,
          fileSize: resp.file_size,
          key: resp.key,
          prefix,
        })
        if (!data?.createQiniuCallback?.attachmentId || !data.createQiniuCallback.previewUrl) {
          console.error(data?.createQiniuCallback)
          throw new Error(data?.createQiniuCallback?.errors?.[0]?.message ?? undefined)
        }
        attachment = {
          contentType: resp.content_type,
          fileHeight: resp.file_height,
          fileWidth: resp.file_width,
          fileName: resp.file_name,
          fileSize: resp.file_size,
          id: data.createQiniuCallback.attachmentId,
          visitUrl: data.createQiniuCallback.previewUrl,
        }
      } catch (error) {
        console.error('failed to create attachment', error)
        upsertItem({ id, status: 'error' })
        throw error
      }

      if (itemsStateRef.current[id]) {
        itemsStateRef.current[id].attachmentId = attachment.id
      }
      upsertItem({ id, status: 'success', attachment })
      successAttachmentsRef.current.push(attachment)
      onSuccessRef.current?.([...successAttachmentsRef.current])
      return attachment
    },
    [upsertItem]
  )

  const removeItem = useCallback((id: string) => {
    setItems(items => items.filter(item => item.id !== id))
    if (itemsStateRef.current[id]) {
      itemsStateRef.current[id].abort()
      delete itemsStateRef.current[id]
    }
    successAttachmentsRef.current = successAttachmentsRef.current.filter(
      attachment => attachment.id !== itemsStateRef.current[id]?.attachmentId
    )
    onSuccessRef.current?.([...successAttachmentsRef.current])
  }, [])

  const removeAll = useCallback(() => {
    setItems([])
    Object.keys(itemsStateRef.current).forEach(id => {
      itemsStateRef.current[id]!.abort()
      delete itemsStateRef.current[id]
    })
    successAttachmentsRef.current = []
    onSuccessRef.current?.([])
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      uploadFile,
      removeUploadItem: removeItem,
      removeAllUploadItems: removeAll,
    }),
    [removeAll, removeItem, uploadFile]
  )

  return (
    children?.({ uploadItems: items, uploadFile, removeUploadItem: removeItem, removeAllUploadItems: removeAll }) ??
    null
  )
}
